// Theme preference: "system" follows prefers-color-scheme; "light"/"dark" pin it. Persisted in
// localStorage (a per-device convenience). index.html applies it before first paint.
import { useSyncExternalStore } from "react";

export type ThemePref = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "mm-theme";
const listeners = new Set<() => void>();

export function parseThemePref(value: unknown): ThemePref {
  return value === "light" || value === "dark" ? value : "system";
}

function readPref(): ThemePref {
  try {
    return parseThemePref(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "system";
  }
}

let pref: ThemePref = typeof window === "undefined" ? "system" : readPref();

function systemDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(p: ThemePref, prefersDark: boolean): ResolvedTheme {
  if (p === "system") return prefersDark ? "dark" : "light";
  return p;
}

export function applyTheme(p: ThemePref = pref): void {
  const root = document.documentElement;
  if (p === "system") delete root.dataset.theme;
  else root.dataset.theme = p;
  root.style.colorScheme = resolveTheme(p, systemDark());
}

function emit() {
  for (const l of listeners) l();
}

export function setThemePref(next: ThemePref): void {
  pref = next;
  try {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // storage unavailable (private window): the choice still applies for this visit
  }
  applyTheme(next);
  emit();
}

/** Flip between light and dark, starting from whatever is on screen now. */
export function toggleTheme(): void {
  setThemePref(resolveTheme(pref, systemDark()) === "dark" ? "light" : "dark");
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const media =
    typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
  const onChange = () => {
    applyTheme();
    listener();
  };
  media?.addEventListener("change", onChange);
  return () => {
    listeners.delete(listener);
    media?.removeEventListener("change", onChange);
  };
}

export function useTheme(): { pref: ThemePref; resolved: ResolvedTheme } {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => `${pref}|${resolveTheme(pref, systemDark())}`,
    () => "system|light",
  );
  const [p, r] = snapshot.split("|") as [ThemePref, ResolvedTheme];
  return { pref: p, resolved: r };
}
